<script>
	/**
	 * This top-level route allows the student to complete an assignment.
	 *
	 * PROPS
	 * - route-context - the context from page.js
	 * - on-ready - a function to call when the route is ready
	 *
	 * SUB ROUTES
	 *
	 * This route has 5 sub-routes (tabs) that change based on user interaction.
	 * These tabs are driven by the tab-manager in student-nav.vue
	 *
	 * These routes are:
	 *
	 * ./student-workspace/text-tab.vue - Allows students to read the article
	 * ./student-workspace/questions-tab.vue - Allows students to answer questions
	 * ./student-workspace/results-tab.vue - (only visible after assignment completed) shows students their grading information
	 * ./student-workspace/vocab-activity-tab.vue - allows students to view do activities to better learn vocabulary words
	 * ./student-workspace/post-submission-tab.vue - a special case, this is only shown right after the student submits
	 *
	 */

	const Vue = require('vue');
	const _ = require('underscore');
	const Q = require('q');
	const productService = require('services/productService');
	const assignmentService = require('services/assignmentService');
	const crudService = require('services/crudService');
	const userContext = require('core/userContext');
	const utils = require('utilities');
	const Store = require('kvstore');
	const page = require('page');
	const model = require('model/model');
	const logger = require('utilities/logger');
	const analyticsUtilities = require('utilities/analyticsUtilities');
	const uaParser = new (require('ua-parser-js'))();

	const TEXT_VIEW = 'text';
	const TEXT_2_VIEW = 'text2';
	const VOCAB_ACTIVITY_VIEW = 'vocab-activity';
	const QUESTIONS_VIEW = 'questions';
	const RESULTS_VIEW = 'results';
	const POST_SUBMISSION_VIEW = 'post-submission';
	const STEPREADS_VIEW = 'sr';
	const STEPREADS_2_VIEW = 'sr2';
	const BOOK_OF_KNOWLEDGE_VIEW = 'book-of-knowledge';
	const EBOOK_VIEW = 'ebook';
	const VIDEO_VIEW = 'video';
	const WORD_DETECTIVE_VIEW = 'word-detective';
	const MEANING_MAPPER_VIEW = 'meaning-mapper';
	const DECODABLES_VIEW = 'decodables';
	const QUESTION_TYPE_TEXT_ANSWER = 1;
	const QUESTION_TYPE_CLICK_OR_TOUCH = 2;
	const QUESTION_TYPE_WHO_WHAT = 3;
	const QUESTION_TYPE_EXTERNAL_PROCESS = 4;
	const QUESTION_TYPE_SPOKEN = 7;

	let i18nMixin = require('i18n/i18nMixin.js');

	const fireEvent = analyticsUtilities.fireEvent;
	const WORKSPACE_TOOL_CLICK = analyticsUtilities.events.WORKSPACE_TOOL_CLICK;
	const WORKSPACE_TAB_CLICK = analyticsUtilities.events.WORKSPACE_TAB_CLICK;
	const GET_CLASS_ASSIGNMENTS = analyticsUtilities.events.GET_CLASS_ASSIGNMENTS;
	const TEACHER_OPEN_WORKSPACE = analyticsUtilities.events.TEACHER_OPEN_WORKSPACE;

	const { authenticateWithGoogleClassroom, isInvalidatingGoogleError } = require('utilities/googleClassroomUtilities');

	const teacherPreviewUtilities = require('utilities/teacherPreviewUtilities');
	const createWorkspaceFromAssignment = teacherPreviewUtilities.createWorkspaceFromAssignment;

	const { getNotifications } = require('utilities/studentNotificationUtilities');

	const {
		getOfflineModeSetting,
		studentSubmitOfflineAssignment,
		updateWorkspaceWithOfflineData,
		getOfflineAssignments
	} = require('utilities/offlineUtilities');

	const { getCustomFontClass, FONT_SIZE_CLASSES, getNextFontSizeClass } = require('utilities/fontUtilities');

	const pubsub = require('events/pubsub');
	const beatriceDataMap = require('beatrice-data-map');

	let isMobile = false;
	let isIpadOS = false;
	try {
		const device = uaParser.getDevice();
		isIpadOS = uaParser.getOS().name === 'Mac OS' && navigator.maxTouchPoints > 0;
		isMobile = device.type === 'mobile' || device.type === 'tablet';
	} catch (e) {
		logger.info("Couldn't detect device type");
	}

	function _makeConfirmSubmitDialogData(view, bok) {
		const activeProduct = view.activeProduct;
		const hasVocabActivity = activeProduct && activeProduct.vocab && activeProduct.vocab.activity;
		const vaAssigned = Array.isArray(view.workspace.opts.vocabularyActivities) && view.workspace.opts.vocabularyActivities.length > 0;
		const qsAssigned = view.workspace.opts.qs.length > 0;
		const bokAssigned = view.workspace.opts.bok && !view.product.decodableTeacherEdition;

		var stats = view.assignmentStats,
			qsCount = 0,
			unanswered = _.map(stats, function (stat, title) {
				qsCount++;
				return {
					qsHeader: 'qs-' + qsCount.toString(),
					title: title,
					unanswered: stat.total - stat.answered
				};
			});

		const confirmInfo = {
			bokBlurb: '',
			qsetBlurb: '',
			vaBlurb: '',
			overallBlurb: '',
			anyway: false
		};

		if (bokAssigned) {
			if (!bok || !bok.trim().length) {
				confirmInfo.bokBlurb = 'You did not write anything in your Book of Knowledge.';
				confirmInfo.anyway = true;
			}
		}

		if (qsAssigned) {
			var unansweredCount = 0;
			_.each(unanswered, function (a) {
				unansweredCount += a.unanswered;
			});
			if (unansweredCount === 1) {
				confirmInfo.qsetBlurb = 'You have one unanswered question.';
				confirmInfo.anyway = true;
			} else if (unansweredCount > 0) {
				confirmInfo.qsetBlurb = 'You have ' + unansweredCount.toString() + ' unanswered questions.';
				confirmInfo.anyway = true;
			}
		}

		if (vaAssigned && hasVocabActivity) {
			// WEB-2538
			const vaScore = view.workspace.scoreState.va;
			const finishedVa = ['wb', 'wc', 'wm'].filter(act => vaScore[act]).length;
			const assignedVa = view.workspace.opts.vocabularyActivities.length;
			const unfinishedVa = assignedVa - finishedVa;
			if (Object.keys(vaScore.rtw.pre).length === 0 || Object.keys(vaScore.rtw.post).length === 0 || unfinishedVa > 0) {
				confirmInfo.vaBlurb = 'You have not finished your vocabulary activities.';
				confirmInfo.anyway = true;
			}
		}

		if (!confirmInfo.anyway && !confirmInfo.qsetBlurb && !confirmInfo.vaBlurb && !confirmInfo.bokBlurb) {
			confirmInfo.overallBlurb = 'Thank you.';
		}

		// if there are no warnings the student needs to look at, then commend them for what is done.
		if (bokAssigned && !confirmInfo.anyway) {
			confirmInfo.bokBlurb = "You've completed your Book of Knowledge entry for this text.";
		}
		if (hasVocabActivity && vaAssigned && !confirmInfo.anyway) {
			confirmInfo.vaBlurb = 'All vocabulary activities complete.';
		}
		if (qsAssigned && !confirmInfo.anyway) {
			confirmInfo.qsetBlurb = 'All Questions Answered.';
		}

		view.confirmSubmitDialogData = confirmInfo;
	}

	/**
	 *
	 * @params view This view.
	 * @return {object} Returns an object that maps a question set title
	 * to an object with the following properties:
	 * 'total': total number of answerable questions (multiple choice or text)
	 * 'answered': total number of answered scorable questions
	 */
	function _computeQuestionStats(view, bok) {
		var workspace = view.workspace,
			assignedQuestionSetIds = workspace.opts.qs,
			expressQuestionSetIds = workspace.opts.expressqs || [],
			product = view.product,
			productQuestionSets = product.questionSets,
			sc = view.workspace.scoreState,
			result = {};
		for (var questionSetId in productQuestionSets) {
			questionSetId = Number(questionSetId);
			if (_.contains(assignedQuestionSetIds, questionSetId)) {
				var qset = productQuestionSets[questionSetId];
				var productQuestions = product.getQuestions(questionSetId, _.contains(expressQuestionSetIds, questionSetId));
				var answeredQSet = sc.q[questionSetId];
				var currQuestionSetStats = {
					total: 0,
					answered: 0
				};
				result[qset.title] = currQuestionSetStats;
				for (var i = 0; i < productQuestions.length; i++) {
					var question = productQuestions[i],
						answeredQuestion = answeredQSet && answeredQSet[question.qid];
					if (
						question.type !== QUESTION_TYPE_EXTERNAL_PROCESS &&
						question.type !== QUESTION_TYPE_CLICK_OR_TOUCH &&
						question.type !== QUESTION_TYPE_SPOKEN
					) {
						currQuestionSetStats.total++;
					}
					if (answeredQuestion) {
						if (question.type === QUESTION_TYPE_WHO_WHAT) {
							var subQuestions = question.subQuestions,
								answeredSubQuestions = answeredQuestion.r,
								answeredCount = 0;
							for (var j = 1; j < subQuestions.length; j++) {
								// skipping first with provided answer
								var subQuestion = subQuestions[j],
									answeredSubQuestion = answeredSubQuestions[subQuestion.qid];
								if (answeredSubQuestion && $.trim(answeredSubQuestion.t || '')) {
									answeredCount++;
								}
							}
							if (answeredCount === subQuestions.length - 1) {
								currQuestionSetStats.answered++;
							}
						} else if (question.type === QUESTION_TYPE_TEXT_ANSWER) {
							if ($.trim(answeredQuestion.t || '')) {
								currQuestionSetStats.answered++;
							}
						} else if (answeredQuestion.aid) {
							currQuestionSetStats.answered++;
						}
					}
				}
			}
		}
		view.assignmentStats = result;
		_makeConfirmSubmitDialogData(view, bok);
	}

	function _maybeSubmitAssignment(view, bok) {
		_computeQuestionStats(view, bok);
		utils.closeModals();
		utils.openModal('#modal-submit-assignment');
	}

	function _submitAssignment(view) {
		let assignment = view.assignment;
		let token = model.get('localGCAccessToken');

		if (view.routeContext.params.pid) {
			// Article-A-Day

			let aid = view.routeContext.params.aid;
			let ws = view.workspace;
			let pid = view.routeContext.params.pid;
			let params = {
				aid,
				pid,
				bok: view.bok || void 0
			};

			if (assignment.gcid || assignment.gca) {
				params.token = token;
			}

			return assignmentService
				.finishStudentArticle(params)
				.then(
					function (result) {
						// the finishStudentArticle call just succeeded, we're about to reload the workspace
						// but we need to semi-optimistically set scoreState.s for this pid to "s" so that we can
						// block double submissions. (semi-optimistic because we already know the submit succeeded
						// we're just not waiting for the whole workspace to come back)
						ws.scoreState.a[pid].s = 's';
						ws.scoreState.a[pid].b = view.bok;
						ws = _.assign({}, ws);

						// students are re-opening the submit modal and re-submitting, so close it and put them
						// at post submission if they're trying to do that.
						utils.closeModals();

						pubsub.publish('message-success', 'Article Finished');

						// view.wsTab = POST_SUBMISSION_VIEW;

						history.replaceState(null, '', '/article-dashboard/' + aid);

						if (ws.opts.mm) {
							page('/article-dashboard/' + aid + '#!articleDashboardTab:meaning-mapper-tab/');
						} else {
							page('/student-workspace/' + aid + '/' + pid + '#!wsTab:post-submission/');
						}
						view.workspace = ws;
						// view.loadAssessmentIfSubmitted();

						// // aid may have changed due to student navigation while the above request was
						// // ongoing, make sure it hasn't before we put the student on post-submission
						// if (view.routeContext.params.aid === aid) {
						// 	Vue.nextTick(function() {
						// 		view.loadWorkspace().then(function() {
						// 			view.loadProduct().then(function() {
						// 				Store.call('$broadcast', 'workspace-loaded', view.workspace, view.product);
						// 			});
						// 		});
						// 	});
						//
						// }

						return result;
					},
					function (err) {
						// if finishStudentArticle failed then get more info by sending an error to track.js
						logger.info('aid is', aid, 'pid is', pid, 'scoreState.s for this article is', ws.scoreState.a[pid].s);
						logger.info('response body', JSON.stringify(err.response && err.response.body));
						logger.error('finishStudentArticle failed with status ' + (err.response && err.response.status));

						if (isInvalidatingGoogleError(err)) {
							model.set('localGCAccessToken', '');
						}

						throw err;
					}
				)
				.all(function () {
					Store.call('$broadcast', 'bok-submitted');
				});
		} else {
			// whatever student answering is going on, wait until that's over before we try to submit
			// the assignment. This is because sometimes a studentAnswerQuestion call can lag and the
			// studentSubmitAssignment goes through ahead of it.
			return view.studentAnsweringPromise.then(function () {
				let params = {
					aid: view.assignmentId
				};

				if (assignment.gcid || assignment.gca) {
					params.token = token;
				}
				return assignmentService
					.studentSubmitAssignment(params)
					.then(function (result) {
						// if student got impatient and reopened submit modal, close it now to make sure they
						// don't submit a second time
						utils.closeModals('#modal-submit-assignment');

						pubsub.publish('message-success', 'Assignment Submitted');
						view.workspace.s = 's';
						Store.call('$broadcast', 'assignment-status', 's'); // Ask student-nav.vue to hide Submit button
						view.wsTab = POST_SUBMISSION_VIEW;
						view.loadAssessmentIfSubmitted();

						view.studentAnsweringPromise = Q();

						return result;
					})
					.catch(err => {
						if (isInvalidatingGoogleError(err)) {
							model.set('localGCAccessToken', '');
						}
					});
			});
		}
	}

	module.exports = {
		props: ['on-ready', 'route-context'],
		components: {
			'questions-tab': require('./student-workspace/questions-tab.vue').default,
			'results-tab': require('./student-workspace/results-tab.vue').default,
			'text-tab': require('./student-workspace/text-tab.vue').default,
			'vocab-activity-tab': require('./student-workspace/vocab-activity-tab.vue').default,
			'post-submission-tab': require('./student-workspace/post-submission-tab.vue').default,
			'book-of-knowledge-tab': require('./student-workspace/book-of-knowledge-tab.vue').default,
			'ebook-tab': require('./student-workspace/ebook-tab.vue').default,
			'video-tab': require('./student-workspace/video-tab.vue').default,
			'word-detective-tab': require('./student-workspace/word-detective-tab.vue').default,
			'meaning-mapper-tab': require('./student-workspace/meaning-mapper-tab.vue').default,
			'language-toggle': require('components/language-toggle.vue').default,
			'overdue-icon': require('components/overdue-icon.vue').default,
			'sentence-frame-icon': require('components/svg/sentence-frame-icon.vue').default,
			'tab-panel': require('components/tab-panel.vue').default,
			'tab-container': require('components/tab-container.vue').default,
			'form-submitter': require('components/form-submitter.vue').default,
			'sentence-frame': require('components/sentence-frame.vue').default
		},
		modelData: [
			'wsTab',
			'splitTab',
			'splitScreen',
			'localGCAccessToken',
			'activeProduct',
			'studentNotifications',
			'offlineMode',
			'offlineAssignments',
			'distractionFreeView',
			'gcAddon'
		],
		mixins: [i18nMixin],
		data() {
			const componentsForSubView = {
				[TEXT_VIEW]: 'text-tab',
				[TEXT_2_VIEW]: 'text-tab',
				[VOCAB_ACTIVITY_VIEW]: 'vocab-activity-tab',
				[QUESTIONS_VIEW]: 'questions-tab',
				[STEPREADS_VIEW]: 'text-tab',
				[STEPREADS_2_VIEW]: 'text-tab',
				[RESULTS_VIEW]: 'results-tab',
				[POST_SUBMISSION_VIEW]: 'post-submission-tab',
				[BOOK_OF_KNOWLEDGE_VIEW]: 'book-of-knowledge-tab',
				[EBOOK_VIEW]: 'ebook-tab',
				[VIDEO_VIEW]: 'video-tab',
				[WORD_DETECTIVE_VIEW]: 'word-detective-tab',
				[MEANING_MAPPER_VIEW]: 'meaning-mapper-tab',
				[DECODABLES_VIEW]: 'text-tab'
			};
			let workspacePreviewClassCode = null;

			if (this.routeContext && this.routeContext.parsedPath && this.routeContext.parsedPath.getQueryParam) {
				workspacePreviewClassCode = this.routeContext.parsedPath.getQueryParam('classCode');
			}

			return {
				studentAnsweringPromise: Q(),
				showAnnotations: true,
				paragraphsNumbered: false,
				submittedAnnotationsCount: 0,
				bok: null,
				componentsForSubView: componentsForSubView,
				assignmentStats: {
					total: 0,
					answered: 0
				}, // assignment statistics calculated when student clicks Submit button
				confirmSubmitDialogData: null, // assignment status data condensed for submit dialog
				TEXT_VIEW: TEXT_VIEW, // contains the article's text
				TEXT_2_VIEW: TEXT_2_VIEW,
				QUESTIONS_VIEW: QUESTIONS_VIEW, // contains the question sets for the article
				STEPREADS_VIEW: STEPREADS_VIEW,
				STEPREADS_2_VIEW: STEPREADS_2_VIEW,
				BOOK_OF_KNOWLEDGE_VIEW: BOOK_OF_KNOWLEDGE_VIEW,
				EBOOK_VIEW: EBOOK_VIEW,
				VIDEO_VIEW: VIDEO_VIEW,
				WORD_DETECTIVE_VIEW: WORD_DETECTIVE_VIEW,
				MEANING_MAPPER_VIEW: MEANING_MAPPER_VIEW,
				RESULTS_VIEW: RESULTS_VIEW, // shows the students their grades
				POST_SUBMISSION_VIEW: POST_SUBMISSION_VIEW, // gives students option to sign off or go back to assignment list
				DECODABLES_VIEW,
				studentName: '', // full name of student
				assignmentId: '', // the assignment id
				workspace: undefined, // the student's workspace with student answers, annotations, etc.
				product: {}, // the product associated with the current workspace
				subArticle: null,
				assessment: {},
				vocabularyWordToEntryMap: {}, // maps a vocabulary word (string) to its entry

				// is equal to the list in product.questionSetsOrdered but filtered so that it only
				// contains question sets that are present in opts
				questionSetsOrderedAndFiltered: [],

				/**
				 * Maps a dictionary name to an object containing definitions for each part-of-speech.
				 * Each part-of-speech object is a map from a word (string) in that part-of-speech
				 * to an array of definitions for that word--each such definition
				 * which contains the 'word', 'sense' index, 'pos' part of speech,
				 * 'text' the text definition, and 'example' and example of its usage.
				 */
				dictionaryDefinitionMap: {
					WCDT: {},
					WLDT: {},
					WEDT: {}
				},
				qSetIndex: 'qs-1', // the currently selected question set (1-origin number prefixed by 'qs-')
				// this will be an index into questionSetsOrderedAndFiltered
				fontSizeClass: FONT_SIZE_CLASSES[0],
				aadArticles: null,
				formParams: {},

				ellFlag: null,

				assignment: null,
				assignmentPastDeadline: false,
				assignmentPastDueDate: false,
				showSentenceFrame: false,
				topFrameLimit: 0,
				bottomFrameLimit: 0,
				clientRect: null,
				sentenceFrameOffsetTop: 0,
				isMobile,
				isIpadOS,
				isToolbarHidden: false,
				isTeacher: !userContext.isStudent(),
				pairedTextModalShown: false,
				workspacePreviewClassCode,
				customFontClass: undefined,
				decodableArticles: [],
				tabProgressed: false
			};
		},
		watch: {
			workspace: {
				handler() {
					this.setSubArticle();
				},
				immediate: true
			},
			offlineAssignmentsMap(value) {
				let aid = this.assignmentId;
				let offlineMode = this.offlineMode;
				if (offlineMode && value && !value[aid]) {
					page('/student');
					pubsub.publish('message-error', 'Assignment not saved for offline.');
				}
			},
			pairedtextModalNeeded(open) {
				if (open) {
					utils.openModal('#pairtext-instructions');
					this.pairedTextModalShown = true;
				}
			}
		},
		computed: {
			pairedtextModalNeeded() {
				return (
					!this.mustRateVocabWordsNow && !this.isTeacher && this.product.productType === 'P' && this.workspace.s !== 's' && !this.pairedTextModalShown
				);
			},
			fromBBOK() {
				let fromBBOK = false;
				let assignmentListState = model.get('studentAssignmentsBackLink');
				if (assignmentListState && assignmentListState.studentTab && assignmentListState.studentTab === 'bbok') {
					fromBBOK = true;
				}
				return fromBBOK;
			},
			paragraphCount() {
				let p = this.subArticle || this.product;
				let content = p && p.content;
				if (!content) {
					return 0;
				}
				let div = document.createElement('DIV');
				div.innerHTML = content;

				return div.querySelectorAll('p').length - div.querySelectorAll('.article-single-main-footer p, figure p').length;
			},
			isAADWorkspace() {
				let product = this.product;
				let assignment = this.assignment;
				return (product && product.productType === 'AAD') || (assignment && assignment.pmd.pt === 'AAD');
			},
			assignmentDone: {
				get() {
					return this.workspace.as && this.workspace.as !== 'a';
				}
			},
			questionsBlurb: {
				get() {
					var ws = this.workspace,
						count = ws.opts.qs.length,
						done = ws.s === 's' || (ws.as && ws.as !== 'a'),
						blurb = 'There ';

					if (count === 1) {
						blurb += done ? 'was one ' : 'is one ';
					} else {
						blurb += done ? 'were ' : 'are ';
						blurb += String(count) + ' ';
					}
					blurb += 'question set';
					if (count > 1) {
						blurb += 's';
					}
					blurb += ' assigned';
					return {
						blurb: blurb,
						count: count
					};
				}
			},
			isEll() {
				let workspace = this.workspace;
				let wsELL = workspace && workspace.opts.ell;
				let studentELL = this.ellFlag && typeof this.ellFlag === 'string';

				return wsELL && studentELL;
			},
			isContentView() {
				const contentViews = new Set([STEPREADS_VIEW, STEPREADS_2_VIEW, TEXT_VIEW, TEXT_2_VIEW, VIDEO_VIEW, DECODABLES_VIEW]);
				return contentViews.has(this.wsTab);
			},
			assignmentSubmitted() {
				let workspace = this.workspace;
				return workspace && (workspace.s !== 'a' || workspace.closed);
			},
			articleIsUnsubmitted() {
				let workspace = this.workspace;
				return (
					(workspace && !workspace.scoreState.a) || !workspace.scoreState.a[this.product.uuid] || workspace.scoreState.a[this.product.uuid].s !== 's'
				);
			},
			showArticle() {
				const workspace = this.workspace;
				const product = this.product;
				const aadArticles = this.aadArticles || [];

				return !!(workspace && workspace.opts.a && product && (product.productType !== 'AAD' || aadArticles.length > 0));
			},
			showArticle2() {
				let workspace = this.workspace;
				return workspace && workspace.opts.a && this.product && this.product.productType === 'P' && !this.product.hasVideo;
			},
			showStepRead1() {
				let workspace = this.workspace;
				if (workspace && workspace.opts.sr) {
					for (var i = 0; i < workspace.opts.sr.length; i++) {
						if (this.product.articles && this.product.articles[workspace.opts.sr[i]].order === 1) {
							return true;
						}
					}
				}

				return false;
			},
			showStepRead2() {
				let workspace = this.workspace;
				if (workspace && workspace.opts.sr) {
					for (var i = 0; i < workspace.opts.sr.length; i++) {
						if (this.product.articles && this.product.articles[workspace.opts.sr[i]].order === 2) {
							return true;
						}
					}
				}

				return false;
			},
			showVocabularyActivities() {
				let workspace = this.workspace;
				let activeProduct = this.activeProduct;
				let assigned = workspace && workspace.opts.vocabularyActivities;

				// Product needs to have VA - WEB-2538
				return activeProduct && activeProduct.vocab && activeProduct.vocab.activity && !!(Array.isArray(assigned) && assigned.length > 0);
			},
			mustRateVocabWordsNow() {
				return this.showVocabularyActivities && Object.keys(this.workspace.scoreState.va.rtw.pre).length === 0;
			},
			showBookOfKnowledge() {
				let workspace = this.workspace;
				let product = this.product;
				return (
					workspace &&
					workspace.opts.bok &&
					product &&
					product.productType !== 'AAD' &&
					workspace.scoreState &&
					workspace.scoreState.a &&
					workspace.scoreState.a[product.uuid] &&
					workspace.scoreState.a[product.uuid].s !== 's' &&
					!product.decodableTeacherEdition
				);
			},
			showQuestions() {
				let workspace = this.workspace;
				return workspace && workspace.opts.qs && workspace.opts.qs.length > 0 && this.product.questionSets;
			},
			showResults() {
				let assignment = this.assignment;
				let allowResults = assignment && assignment.shwres !== false;
				let workspace = this.workspace;
				let offlineMode = this.offlineMode;
				return offlineMode === false && (this.product.productType === 'AAD' || (allowResults && workspace && (workspace.s === 's' || workspace.closed)));
			},
			showWordDetective() {
				let workspace = this.workspace;
				return this.product.productType === 'AAD' && workspace && workspace.opts.wd;
			},
			showMeaningMapper() {
				let workspace = this.workspace;
				return this.product.productType === 'AAD' && workspace && workspace.opts.mm;
			},
			showEBook() {
				let workspace = this.workspace;
				return workspace && workspace.opts.ebook && this.product.ebook;
			},
			showVideo() {
				let workspace = this.workspace;
				return (workspace && workspace.opts.video) || (this.product.productType === 'P' && this.product.hasVideo);
			},
			showDecodables() {
				const workspace = this.workspace;
				return workspace && workspace.opts.decodables && this.product.dteGroups;
			},
			contentTabs() {
				const showArticle = this.showArticle;
				const showArticle2 = this.showArticle2;
				const showStepRead1 = this.showStepRead1;
				const showStepRead2 = this.showStepRead2;
				const showVocabularyActivities = this.showVocabularyActivities;
				const showBookOfKnowledge = this.showBookOfKnowledge;
				const showQuestions = this.showQuestions;
				const showResults = this.showResults;
				const showEBook = this.showEBook;
				const showVideo = this.showVideo;
				const showWordDetective = this.showWordDetective;
				const showMeaningMapper = this.showMeaningMapper;
				const splitScreen = this.splitScreen;
				const product = this.product;
				const offlineMode = this.offlineMode;
				const showDecodables = this.showDecodables;
				const mustProgressTabNow = this.mustProgressTabNow;
				let tabs = [];
				const vocabActivitiesTab = {
					id: VOCAB_ACTIVITY_VIEW,
					text: 'Vocabulary Activity',
					offline: true
				};

				if (typeof offlineMode === 'undefined') {
					return tabs;
				}

				if (this.mustRateVocabWordsNow) {
					return [vocabActivitiesTab];
				}

				if (showResults) {
					tabs.push({
						id: RESULTS_VIEW,
						text: 'Results'
					});
				}
				if (showArticle) {
					let text = 'Passage';
					if (product.productType === 'AAD') {
						text = 'Articles';
					} else if (product.productType === 'P' && !showVideo) {
						text += ' 1';
					}
					tabs.push({
						id: TEXT_VIEW,
						text,
						offline: true
					});
				}
				if (showArticle2) {
					tabs.push({
						id: TEXT_2_VIEW,
						text: 'Passage 2',
						offline: true,
						disabled: mustProgressTabNow,
						disabledTooltip: 'Read Passage 1 to gain access to Passage 2'
					});
				}

				if (showVideo) {
					tabs.push({
						id: VIDEO_VIEW,
						text: 'Video'
					});
				}

				if (showStepRead1) {
					tabs.push({
						id: STEPREADS_VIEW,
						text: 'SR1',
						offline: true
					});
				}
				if (showStepRead2) {
					tabs.push({
						id: STEPREADS_2_VIEW,
						text: 'SR2',
						offline: true
					});
				}

				if (showDecodables && this.decodableArticles.length > 0) {
					tabs.push({
						id: DECODABLES_VIEW,
						text: 'Decodables',
						offline: true
					});
				}

				if (showVocabularyActivities && !splitScreen) {
					tabs.push(vocabActivitiesTab);
				}

				if (showBookOfKnowledge && !splitScreen) {
					tabs.push({
						id: BOOK_OF_KNOWLEDGE_VIEW,
						text: 'Book of Knowledge',
						offline: true
					});
				}

				if (showQuestions && !splitScreen) {
					tabs.push({
						id: QUESTIONS_VIEW,
						text: 'Questions',
						offline: true,
						disabled: mustProgressTabNow,
						disabledTooltip: 'Read Passage 1 to gain access to the Questions'
					});
				}

				if (showEBook && !splitScreen) {
					tabs.push({
						id: EBOOK_VIEW,
						text: 'eBook'
					});
				}

				if (showWordDetective) {
					tabs.push({
						id: WORD_DETECTIVE_VIEW,
						text: 'Word Detective'
					});
				}

				if (showMeaningMapper) {
					tabs.push({
						id: MEANING_MAPPER_VIEW,
						text: 'Meaning Mapper'
					});
				}

				if (offlineMode) {
					tabs = tabs.filter(t => t.offline);
				}

				return tabs;
			},
			splitTabs() {
				let allowSplitScreen = this.allowSplitScreen;
				let showBookOfKnowledge = this.showBookOfKnowledge;
				let showQuestions = this.showQuestions;
				let showVocabularyActivities = this.showVocabularyActivities;
				let tabs = [];

				if (allowSplitScreen) {
					if (showBookOfKnowledge) {
						tabs.push({
							id: BOOK_OF_KNOWLEDGE_VIEW,
							text: 'Book of Knowledge'
						});
					}
					if (showVocabularyActivities) {
						tabs.push({
							id: VOCAB_ACTIVITY_VIEW,
							text: 'Vocabulary Activity'
						});
					}
					if (showQuestions) {
						tabs.push({
							id: QUESTIONS_VIEW,
							text: 'Questions'
						});
					}
				}
				return tabs;
			},
			allowSplitScreen() {
				const workspace = this.workspace;
				const resultsView = (workspace && (workspace.s === 's' || workspace.closed)) || this.product.productType === 'AAD';
				const showBookOfKnowledge = this.showBookOfKnowledge;
				const showQuestions = this.showQuestions;
				const wsTab = this.wsTab;
				const mustRateVocabWordsNow = this.mustRateVocabWordsNow;
				const mustProgressTabNow = this.mustProgressTabNow;

				// Is not results and has questions or BoK
				return !mustRateVocabWordsNow && !mustProgressTabNow && !resultsView && wsTab !== 'ebook' && (showQuestions || showBookOfKnowledge);
			},
			showSplitPanel() {
				let allowSplitScreen = this.allowSplitScreen;
				let splitScreen = this.splitScreen;

				return allowSplitScreen && splitScreen;
			},
			assignmentListState() {
				let assignmentListState = model.get('studentAssignmentsBackLink');
				let state = '#!';
				if (assignmentListState) {
					Object.keys(assignmentListState).forEach(k => {
						state += k + ':' + assignmentListState[k] + '/';
					});
				}

				return state;
			},
			needsGoogleAuth() {
				let offlineMode = this.offlineMode;
				let token = this.localGCAccessToken;
				let assignment = this.assignment;

				return offlineMode === false && assignment && (assignment.gcid || (assignment.gca && this.gcAddon)) && !token;
			},
			markedDone() {
				let studentNotifications = this.studentNotifications;
				let assignmentId = this.assignmentId;
				let workspace = this.workspace;
				let done = false;

				if (workspace && workspace.s === 's' && studentNotifications && assignmentId) {
					done = typeof studentNotifications[assignmentId] !== 'undefined';
				}
				return done;
			},
			offlineAssignmentsMap() {
				let offlineAssignments = this.offlineAssignments;
				let userContextData = userContext.getUserData();
				let uid = userContextData ? userContextData.uid : null;
				let classCode = userContextData ? userContextData.classCode : null;
				let map = {};

				if (uid && offlineAssignments && offlineAssignments[uid]) {
					let allMap = offlineAssignments[uid];
					map = Object.keys(allMap).reduce((acc, aid) => {
						if (allMap[aid].cc === classCode) {
							acc[aid] = allMap[aid];
						}
						return acc;
					}, {});
				}
				return map;
			},
			articleList() {
				return this.wsTab === 'decodables' ? this.decodableArticles : this.aadArticles;
			},
			mustProgressTabNow() {
				const product = this.product;

				if (product.productType !== 'P' || product.subType === 8000 || this.tabProgressed) {
					return false;
				}

				const workspace = this.workspace;
				const hasAnsweredQuestion = Object.values(workspace.scoreState.q || {})
					.flatMap(qs => Object.values(qs))
					.some(question => question.aid || question.t);

				return !hasAnsweredQuestion;
			}
		},
		methods: {
			setSubArticle() {
				var wsTab = this.wsTab;

				if (this.workspace) {
					if (this.product.articles && !this.product.sr) {
						if (wsTab === 'text') {
							if (this.isAADWorkspace) {
								for (var i = 0, l = this.product.articleList.length; i < l; i++) {
									if (
										this.workspace.scoreState.a[this.product.articleList[i].uuid] &&
										this.workspace.scoreState.a[this.product.articleList[i].uuid].s === 's'
									) {
										this.subArticle = this.product.articleList[i];

										break;
									}
								}
							} else {
								this.subArticle = this.product.articleList[0];
							}
						} else if (wsTab === 'text2' || wsTab === 'video') {
							this.subArticle = this.product.articleList[1];
						} else if (wsTab === DECODABLES_VIEW && this.decodableArticles.length > 0) {
							this.subArticle = this.decodableArticles[0];
						}
					} else if (this.product.sr) {
						if (wsTab === 'sr') {
							this.subArticle = _.find(this.product.articles, function (article) {
								return article.order === 1;
							});
						} else if (wsTab === 'sr2') {
							this.subArticle = _.find(this.product.articles, function (article) {
								return article.order === 2;
							});
						} else {
							this.subArticle = null;
						}
					}
				}
			},

			/**
			 * Looks at the fontSizeClass property and assigns to it class at the
			 * next index in FONT_SIZE_CLASSES (wrapping around to 0 from the last)
			 */
			nextFontSizeClass() {
				const { fontSizeClass, state } = getNextFontSizeClass(this.fontSizeClass);

				this.fontSizeClass = fontSizeClass;
				const action_type = 'font_size';

				this.workspaceToolClickEvent({
					state,
					action_type
				});
				pubsub.publish('font-size-changed');
			},
			submitAssignment: function submitAssignment(event) {
				if (event) {
					event.preventDefault();
				}

				pubsub.publish('submit-assignment');
			},
			doSubmitAssignment: function doSubmitAssignment() {
				utils.closeModals();
				model.set('splitScreen', false);

				if (this.offlineMode) {
					let aid = this.routeContext.params.aid;
					let pid = this.routeContext.params.pid || undefined;
					let params = {
						aid,
						pid,
						bok: this.bok || void 0,
						ts: Date.now()
					};

					return studentSubmitOfflineAssignment(params).then(() => {
						utils.openModal('#modal-offline-assignment-submitted');
						page('/student');
					});
				} else {
					return _submitAssignment(this);
				}
			},
			isQuestionSetAssigned(questionSetId) {
				return _.contains(this.workspace.opts.qs, questionSetId);
			},
			scrollToTop: function scrollToTop() {
				$(window).scrollTop(0);
			},
			selectArticle(article) {
				this.subArticle = article;
			},
			/**
			 * Part of the data loading process started by compile, loads self.workspace.
			 *
			 * @return a promise that is resolved when the workspace is loaded and self.workspace is set
			 */
			loadWorkspace() {
				var self = this;
				var deferred = Q.defer();
				var assignmentId = self.routeContext.params.aid;
				let offlineMode = this.offlineMode;

				if (!this.isTeacher) {
					assignmentService
						.studentOpenWorkspace({
							aid: assignmentId
						})
						.then(workspace => {
							if (workspace) {
								if (workspace.as === 'd') {
									page('/student');
									pubsub.publish('message-error', 'Your teacher has just deleted that assignment');
								}

								if (workspace.opts.ell) {
									self.ellFlag = userContext.getELLFlag();
								}

								if (offlineMode) {
									updateWorkspaceWithOfflineData(workspace).then(updatedWorkspace => {
										self.workspace = updatedWorkspace;
										deferred.resolve();
									});
								} else {
									self.workspace = workspace;
									deferred.resolve();
								}
							} else {
								pubsub.publish('message-error', 'Failed to load assignment');
								deferred.reject();
							}
						})
						.catch(function (error) {
							if (error.code === 1023) {
								// Class was deleted out from under you
								page('/logout');
							} else {
								page('/student');
							}
						});
				} else {
					deferred.resolve(null);
				}

				return deferred.promise;
			},
			updateWorkspaceGCASubmissionId() {
				const needsUpdate = !this.isTeacher && this.gcAddon && !this.workspace.gcaSubmissionId;

				if (!needsUpdate) {
					return Promise.resolve();
				}

				return assignmentService
					.updateGCASubmissionId({
						aid: this.routeContext.params.aid,
						iid: this.gcAddon.itemId,
						token: this.localGCAccessToken
					})
					.then(submissionId => {
						this.workspace.gcaSubmissionId = submissionId;
					})
					.catch(e => {
						if (e.code === 1063) {
							page('/logout');
						}
					});
			},
			/**
			 * Part of the data loading process started by compile, loads self.assignment.
			 * The assignment is loaded to check for deadline dates
			 *
			 * @return a promise that completes when the assignment is loaded
			 */
			loadAssignment() {
				let classCode = null;

				if (this.isTeacher) {
					if (this.workspacePreviewClassCode) {
						classCode = this.workspacePreviewClassCode;
					} else {
						const target = this.gcAddon ? '/classroom-addon' : '/student-authentication';
						page(target);
						return Q();
					}
				} else {
					classCode = userContext.getStudentClassCode();
				}

				fireEvent(GET_CLASS_ASSIGNMENTS, {
					aid: this.routeContext.params.aid,
					cc: classCode
				});

				return crudService
					.read('classAssignments', {
						aid: this.routeContext.params.aid,
						cc: classCode
					})
					.then(result => {
						let prms = Q(result);
						if (this.isTeacher) {
							prms = createWorkspaceFromAssignment(result).then(workspace => {
								this.workspace = workspace;
								return result;
							});
						}
						return prms;
					})
					.then(result => {
						// AAD metadata needs allArticles
						if (result && result.pmd && result.pmd.pt === 'AAD') {
							const mappedPMD = beatriceDataMap.product.serverToClient(result.pmd);

							result.pmd.allArticles = mappedPMD.allArticles;
						}
						this.assignment = result;

						let now = new Date().getTime() / 1000;

						let duedate = this.workspace.edd || result.edd;

						if (result && result.deadline && result.deadline < now) {
							this.assignmentPastDeadline = true;
						}

						if (duedate) {
							// Use student specific edd over assignment
							if (this.workspace.s === 's' && duedate < this.workspace.ts) {
								// Student submitted past the deadline
								this.assignmentPastDueDate = true;
							} else if (this.workspace.s !== 's' && duedate < now) {
								// Student hasn't submitted and deadline is past
								this.assignmentPastDueDate = true;
							}
						}

						return result;
					});
			},
			/**
			 * Part of the data loading process started by compile, loads self.product.
			 * The product will also be used to augment the contents of self.workspace.
			 *
			 * @return a promise that is resolved when the product is loaded and self.product is set
			 */
			loadProduct() {
				var self = this;
				let isTeacher = this.isTeacher;

				model.set('activeProduct', null);

				if (!self.workspace || !self.workspace.productId) {
					return Q();
				}

				return productService.get(self.workspace.productId).then(function (product) {
					var ws = self.workspace;
					var scq = isTeacher ? null : self.workspace.scoreState.q;

					// If AAD, set entire product as activeProduct
					model.set('activeProduct', product);

					if (self.routeContext.params.pid && self.routeContext.params.pid !== self.workspace.productId) {
						// viewing a sub article out of compound product (e.g. AAD)
						let pid = self.routeContext.params.pid;
						if (product.articles[pid]) {
							product = product.articles[pid];
						} else {
							let decodableProduct;
							if (ws.opts.decodables && product.dteGroups) {
								const assignedDecodables = new Set(ws.opts.decodables);
								for (const dteGroup of product.dteGroups) {
									if (assignedDecodables.has(dteGroup.id)) {
										decodableProduct = dteGroup.articles[pid];

										if (decodableProduct) {
											product = decodableProduct;
											break;
										}
									}
								}
							}

							if (!decodableProduct && product.supplemental_texts) {
								if (ws.opts.challenge && product.supplemental_texts.challenge && product.supplemental_texts.challenge[pid]) {
									product = product.supplemental_texts.challenge[pid];
								} else if (ws.opts.support && product.supplemental_texts.support && product.supplemental_texts.support[pid]) {
									product = product.supplemental_texts.support[pid];
								}
							}
						}
					}

					self.product = product;

					// populate any missing questionset/question entries in the
					// scoreState
					if (scq) {
						_.each(product.questionSets, function (qs) {
							scq[qs.qsid] = scq[qs.qsid] || {};
							_.each(qs.questions, function (q) {
								scq[qs.qsid][q.qid] = scq[qs.qsid][q.qid] || {};
							});
						});
					}

					// populate questionSetsOrderedAndFiltered so we have a clean
					// list of question sets to iterat with
					var qsOpts = ws && ws.opts && ws.opts.qs && ws.opts.qs.length ? ws.opts.qs : [];
					self.questionSetsOrderedAndFiltered = _.filter(product.questionSetsOrdered, function (qs) {
						return _.contains(qsOpts, qs.qsid);
					});

					self.setSubArticle();
				});
			},
			/**
			 * Part of the data loading process started by compile, loads self.assessment, but only if
			 * workspace.s==='s', otherwise it resolves instantly
			 *
			 * @return a promise that is resolved when the assessment is loaded or when we see we don't need it
			 */
			loadAssessmentIfSubmitted() {
				if (!this.workspace || (this.workspace.s !== 's' && !this.workspace.closed)) {
					return Q();
				}

				return assignmentService
					.teacherGetSingleStudentAssignmentResults({
						classCode: this.workspace.cc,
						assignmentId: this.routeContext.params.aid,
						uid: this.workspace.uid
					})
					.then(assessment => {
						this.assessment = assessment;
						if (!this.studentNotifications) {
							return getNotifications();
						}
						return null;
					});
			},
			toggleAnnotations() {
				this.showAnnotations = !this.showAnnotations;
				let action_type = 'show_annotations';
				this.workspaceToolClickEvent({
					action_type,
					state: this.showAnnotations
				});
			},
			toggleParagraphNumbers() {
				this.paragraphsNumbered = !this.paragraphsNumbered;
				let action_type = 'show_paragraph_numbers';
				this.workspaceToolClickEvent({
					action_type,
					state: this.paragraphsNumbered
				});
			},
			toggleSentenceFrame() {
				this.showSentenceFrame = !this.showSentenceFrame;
				let action_type = 'show_sentence_frame';
				this.workspaceToolClickEvent({
					action_type,
					state: this.showSentenceFrame
				});
			},
			setLanguage(code) {
				// Still call mixin provided method
				i18nMixin.methods.setLanguage.apply(this, arguments);
				let action_type = 'toggle_language';

				this.workspaceToolClickEvent({
					action_type,
					state: code
				});
			},
			workspaceToolClickEvent(data) {
				_.assign(data, this.getEventData());

				if (data.value) {
					data.state = data.value;
					delete data.value;
				}

				fireEvent(WORKSPACE_TOOL_CLICK, data);
			},
			workspaceTabClick: function (e) {
				let data = this.getEventData();

				if (e && e.target) {
					// Use updated tab, as this is from a tab click.
					_.assign(data, {
						tab: e.target.getAttribute('data-tab')
					});
				}
				fireEvent(WORKSPACE_TAB_CLICK, data);
			},
			getEventData() {
				let tab = this.wsTab;
				let aid = this.assignmentId;
				let ss = this.splitScreen || undefined;
				let pid = (this.subArticle && this.subArticle.uuid) || this.product.uuid;

				return {
					tab,
					aid,
					pid,
					ss
				};
			},
			toggleSplitScreen() {
				const splitTabs = this.splitTabs;
				const wsTab = this.wsTab;
				const splitScreen = !this.splitScreen;
				const selectedSplitTab = splitTabs.find(t => t.id === wsTab);
				const questionsTab = splitTabs.find(t => t.id === QUESTIONS_VIEW);

				if (selectedSplitTab) {
					const { productType, subType } = this.product;
					const targetWSTab = productType === 'P' && subType !== 8000 ? 'text2' : 'text';

					model.set('wsTab', targetWSTab);
				}

				const action_type = 'toggle_splitscreen';
				this.workspaceToolClickEvent({
					action_type,
					state: splitScreen
				});

				Vue.nextTick(() => {
					model.set('splitScreen', splitScreen);
					model.set('splitTab', (selectedSplitTab && selectedSplitTab.id) || (questionsTab && questionsTab.id) || (splitTabs[0] && splitTabs[0].id));
				});
			},
			toggleToolbar() {
				let value = !this.isToolbarHidden;
				this.isToolbarHidden = value;

				let action_type = 'toggle_hide_toolbar';
				this.workspaceToolClickEvent({
					action_type,
					state: value
				});
			},
			toggleDistractionFree() {
				let value = !model.get('distractionFreeView');
				model.set('distractionFreeView', value);

				let action_type = 'toggle_distraction_free';
				this.workspaceToolClickEvent({
					action_type,
					state: value
				});
			},
			authenticateWithGoogle() {
				authenticateWithGoogleClassroom();
			},
			onNextQuestionSet() {
				let splitScreen = this.splitScreen;
				let tabs = this.$refs.rightTabs;

				if (splitScreen) {
					tabs = this.$refs.leftTabs;
				}

				tabs.scrollToTop();
			},
			setFont() {
				const { rmFont } = userContext.getUserData();

				this.customFontClass = getCustomFontClass(rmFont);
			},
			onNextWorkspaceTab() {
				this.tabProgressed = true;
				let tabs = this.contentTabs;
				let tabKey = 'wsTab';
				let foundTab = false;

				if (this.splitScreen) {
					tabs = this.splitTabs;
					tabKey = 'splitTab';
				}

				for (let t = 0; t < tabs.length; t++) {
					if (foundTab) {
						model.set(tabKey, tabs[t].id);
						break;
					}
					if (this[tabKey] === tabs[t].id) {
						foundTab = true;
					}
				}
			}
		},
		created() {
			var self = this;
			let aid = self.routeContext.params.aid;

			self.studentName = userContext.getFullName();
			self.assignmentId = aid;

			// There have been a few cases of users submitting assignments with a blank
			// assignmentId. Submitting assignments is only done in 1 place using this
			// variable. This variable is only set in this 1 place. It seems possible
			// that somehow this variable is getting initialized when the routeContext
			// contains a blank aid, so we'll log it if it happens and log the
			// pathname so we can see where they're coming from
			if (!self.assignmentId) {
				logger.error('blank assignment ID, initializing student-workspace while on path', self.routeContext.canonicalPath);
			}

			getOfflineModeSetting()
				.finally(mode => {
					return self
						.loadWorkspace()
						.then(self.updateWorkspaceGCASubmissionId)
						.then(self.loadAssignment)
						.then(self.loadProduct)
						.then(self.loadAssessmentIfSubmitted)
						.then(function () {
							self.$emit('data-ready');
							Store.call('$broadcast', 'workspace-loaded', self.workspace, self.product);
						})
						.done();
				})
				.done();

			// we will chain all student answer question calls' promises. This way we can ensure that
			// a submit will happen only after all the student answers are done.
			self.$subscribe('student-answer-question-promise', function (promise) {
				self.studentAnsweringPromise = self.studentAnsweringPromise.then(function () {
					return promise;
				});
			});
		},
		mounted() {
			const self = this;

			var getSubmittedTotalAnnotationCount = _.debounce(function () {
				var wsTab = self.wsTab;
				if (wsTab !== 'text' && wsTab !== 'text2' && wsTab !== 'sr' && wsTab !== 'sr2') {
					return;
				}

				if (!self.product || !self.product.uuid || !self.routeContext.params.aid || self.workspace.s !== 's' || self.workspace.closed) {
					return;
				}

				var productId = (self.subArticle && self.subArticle.uuid) || self.product.uuid;

				if (self.offlineMode === false) {
					assignmentService
						.studentGetAnnotations({
							assignmentId: self.routeContext.params.aid,
							productId: productId
						})
						.then(function (annotationsForProduct) {
							annotationsForProduct = annotationsForProduct || {};
							self.submittedAnnotationsCount = Object.keys(annotationsForProduct).length;
						});
				}
			}, 100);

			self.$watch('offlineMode', function (value) {
				if (value === false) {
					self.submittedAnnotationsCount = 0;
					getSubmittedTotalAnnotationCount();
				}
			});

			self.$watch('product', function () {
				self.submittedAnnotationsCount = 0;
				getSubmittedTotalAnnotationCount();
			});

			self.$watch('wsTab', function () {
				self.setSubArticle();
				self.submittedAnnotationsCount = 0;
				getSubmittedTotalAnnotationCount();
			});

			if (!this.isTeacher) {
				self.$watch('workspace.s', function () {
					self.submittedAnnotationsCount = 0;
					getSubmittedTotalAnnotationCount();
				});

				this.setFont();
			}

			self.$watch('routeContext.params.pid', function (val) {
				if (val) {
					var workspace = this.workspace;

					if (
						workspace &&
						this.routeContext.params.pid &&
						workspace.scoreState &&
						workspace.scoreState.a &&
						workspace.scoreState.a[this.routeContext.params.pid].s === 's'
					) {
						page('/student');
					}
				}
				self.loadProduct();
			});

			self.$watch('product', function (val) {
				let wsTab = this.wsTab;

				if (val.articles && !val.sr) {
					if (wsTab !== 'text2' && wsTab !== 'video') {
						if (val.productType === 'AAD') {
							var workspace = self.workspace;

							if (workspace) {
								let subArticle = null;
								let subArticleLink = model.get('subArticleLink');

								if (subArticleLink && this.workspace.scoreState.a[subArticleLink] && this.workspace.scoreState.a[subArticleLink].s === 's') {
									val.articleList.every(a => {
										if (a.uuid === subArticleLink) {
											subArticle = a;
											model.remove('subArticleLink');
											return false;
										}
										return true;
									});
								}

								let articleList = Object.keys(val.allArticles)
									.sort()
									.map(pid => {
										return val.allArticles[pid];
									});

								const { aadArticles, decodableArticles } = articleList.reduce(
									(acc, article) => {
										const scoreState = workspace.scoreState.a[article.uuid];
										const isSubmitted = scoreState && scoreState.s === 's';

										if (this.assignmentPastDeadline || isSubmitted) {
											if (article.decodableTeacherEdition) {
												acc.decodableArticles.push(article);
											} else {
												acc.aadArticles.push(article);
											}
										}

										return acc;
									},
									{
										aadArticles: [],
										decodableArticles: []
									}
								);

								this.aadArticles = aadArticles;
								this.decodableArticles = decodableArticles;

								if (wsTab === DECODABLES_VIEW) {
									subArticle = decodableArticles[0];
								}

								if (!subArticle) {
									subArticle = self.aadArticles[0];
								}

								self.subArticle = subArticle;
							}
						} else {
							this.subArticle = val.articleList[0];
						}
					} else {
						this.subArticle = val.articleList[1];
					}
				} else if (val.sr) {
					if (wsTab === 'sr') {
						this.subArticle = _.find(val.articles, function (article) {
							return article.order === 1;
						});
					} else if (wsTab === 'sr2') {
						this.subArticle = _.find(val.articles, function (article) {
							return article.order === 2;
						});
					}
				}

				if (this.contentTabs && this.contentTabs.length === 1) {
					wsTab = this.contentTabs[0].id;
					model.set('wsTab', wsTab);
				}

				if (wsTab !== 'text') {
					this.tabProgressed = true;
				}

				if (this.isTeacher) {
					let dismissedAssignment = model.get('workspacePreviewModalAssignment') === this.assignmentId;
					let dismissedModal = model.get('workspacePreviewModalDismiss');
					if (!dismissedModal && !dismissedAssignment) {
						utils.openModal('#modal-workspace-preview');
					}
				}
			});

			model.set('splitScreen', false);

			pubsub.subscribe('submit-assignment', bok => {
				if (this.isTeacher) {
					utils.openModal('#modal-teacher-end-preview');
					return true;
				}

				_maybeSubmitAssignment(this, bok);

				this.bok = bok;

				return true;
			});

			pubsub.subscribe('vocab-activity-complete', completionInfo => {
				self.workspace.scoreState.va = _.assign({}, self.workspace.scoreState.va, completionInfo);
			});

			pubsub.subscribe('vocab-rtw-submitted', ratingInfo => {
				const ws = _.assign({}, self.workspace);
				ws.scoreState.va.rtw = _.assign({}, ws.scoreState.va.rtw, ratingInfo);
				self.workspace = ws;

				// just finished the "pre" rating, all other tabs were disabled, now that
				// they are shown go to the first tab
				if (ratingInfo.pre) {
					model.set('wsTab', this.contentTabs[0].id);
				} else if ((ws.s === 'a' && ws.opts && !ws.opts.qs) || ws.opts.qs.length == 0) {
					this.submitAssignment();
				}
			});

			getOfflineAssignments();

			if (userContext.isTeacher()) {
				const aid = this.routeContext.params.aid;
				analyticsUtilities.fireEvent(TEACHER_OPEN_WORKSPACE, { aid });
			}
		},
		beforeDestroy() {
			model.set('activeProduct', null);
			model.set('splitScreen', false);
			Store.call('$broadcast', 'workspace-loaded');

			model.remove('wsTab');
			model.remove('distractionFreeView');
		}
	};
</script>
<style lang="scss">
	@import 'config/variables';
	@import 'config/colors';
	@import 'mixins/flexbox';

	main.main.student-workspace {
		@include flex(1);
		@include flexbox();
		@include flex-direction(column);
		min-height: 0px;

		.main-header {
			position: sticky;
			top: 0;
			z-index: 1;
			padding-bottom: 0;

			.wrapper {
				.main-header-back-wrapper {
					display: none;
				}

				.main-header-message {
					height: 100%;
				}

				.main-header-content {
					.paired-text-no-question-set-submit {
						position: absolute;
						right: 0.25rem;
						top: 0.25rem;
						z-index: 1;
					}
				}

				.main-header-right {
					position: fixed;
					bottom: 0;
					left: 0;
					right: 0;
					z-index: 1;
					text-align: center;

					.toolbar-hide-tab {
						position: absolute;
						z-index: 1;
						height: 2.25rem;
						width: 2.5rem;
						top: 0.5rem;
						left: 0.25rem;
						font-size: 2rem;
						background-color: #f5f8f8;
						color: $c-slate-dark;

						i {
							display: block;
							padding: 0.1rem;
						}
					}

					&.hidden-toolbar {
						opacity: 0.85;

						.main-header-actions {
							height: 0;
							padding: 0;
						}

						.toolbar-hide-tab {
							top: -2.25rem;
							background-color: #f5f8f8;
							border: 1px solid #dcdee0;
							border-radius: 5px 5px 0 0;
							border-bottom-width: 0;

							i {
								display: block;
								padding: 0.1rem;
							}
						}
					}

					.language-toggle-container {
						position: absolute;
						right: 1rem;
						top: 1rem;
					}
				}

				.main-header-actions {
					position: relative;
					background-color: $c-blue-lighter;
					border-top: 1px solid $c-grey;
					bottom: 0;
					right: 0;
					padding: 0.25rem 0;

					.button.toolbar-button {
						position: relative;
					}
				}
			}
		}

		.auto-save {
			color: $c-slate-dark;
			text-align: right;
		}

		.workspace-body {
			@include flexbox();
			@include flex(1);
			min-height: 0px;

			.content-panel {
				.content-panel-body {
					position: relative;
				}
			}

			.tab-panel {
				flex: 1;

				.tab-panel-tabs {
					display: none;
				}
			}
		}

		.split-screen & {
			.workspace-body {
				.content-panel {
					margin-right: 0.5rem;
				}
			}
		}

		.distraction-free-button {
			display: none;
		}
	}

	// Tablet + desktop
	@media only screen and (min-width: $bp-width-med) {
		main.main.student-workspace {
			.main-header {
				padding-bottom: 0.5rem;

				.wrapper {
					position: relative;

					.main-header-content,
					.main-header-top {
						@include flexbox();
						@include align-items(center);

						.main-header-mobile-nav {
							display: none;
						}
					}

					.main-header-content {
						.paired-text-no-question-set-submit {
							position: relative;
							margin: 0 0.25rem;
							top: 0;
						}
					}

					.main-header-back-wrapper {
						display: none;
						@include flexbox();
						@include justify-content(center);
						@include flex-direction(column);

						.main-header-back {
							float: none;
							margin-top: 0rem;
							margin-bottom: 0rem;
						}
					}

					.main-header-message {
						@include flexbox();
						@include justify-content(center);
						@include align-items(center);
						margin: 0rem 1rem;
					}

					.main-header-right {
						position: relative;
						display: none;
						@include flex(1);
						@include flexbox();
						@include flex-direction(column);
						@include justify-content(center);

						.main-header-actions {
							@include flexbox();
							@include justify-content(flex-end);
							float: none;
							border-top-width: 0px;

							.button {
								margin-right: $size-xs;
								min-height: 42px;
							}
						}

						.toolbar-hide-tab {
							display: none;
						}

						.language-toggle-container {
							text-align: right;
							position: relative;
							top: 0;
							right: 0;
						}
					}
				}
			}

			.workspace-body {
				.tab-panel {
					.tab-panel-tabs {
						min-height: 60px;
						@include flexbox();
					}

					.tab-panel-body {
						overflow: auto;
						border-width: 0px 1px 1px 1px;
					}
				}
			}

			.distraction-free-button {
				display: inline;
			}
		}
	}

	@media only screen and (max-width: $bp-width-med) {
		main.main.student-workspace {
			.tab-panel {
				border: none;
			}
		}
	}
</style>
<template>
	<main
		v-if="workspace"
		:class="['main', 'student-workspace', wsTab]"
		v-scroll-target="content - target">
		<header
			ref="header"
			class="main-header">
			<div class="wrapper">
				<div class="main-header-content">
					<div class="main-header-back-wrapper">
						<a
							v-if="
								!gcAddon &&
								!isTeacher &&
								(!isAADWorkspace ||
									wsTab === POST_SUBMISSION_VIEW ||
									(workspace && workspace.s === 's') ||
									workspace.closed ||
									assignmentPastDeadline ||
									(aadArticles && aadArticles.length) ||
									(decodableArticles && decodableArticles.length))
							"
							class="main-header-back"
							:href="`/student${assignmentListState}`">
							<template v-if="!fromBBOK"> &larr; Assignments </template>
							<template v-if="fromBBOK"> &larr; My Big Book of Knowledge </template>
						</a>
						<a
							v-if="isAADWorkspace && routeContext.params.pid && wsTab !== POST_SUBMISSION_VIEW"
							class="main-header-back"
							:href="`/article-dashboard/${assignmentId}${workspacePreviewClassCode ? '?classCode=' + workspacePreviewClassCode : ''}`">
							&larr; Articles
						</a>
					</div>
					<div
						class="main-header-back-wrapper"
						v-if="isTeacher && !isAADWorkspace">
						<a
							class="main-header-back"
							:href="`/assignment/${workspacePreviewClassCode}/${assignmentId}/classresults`">
							&larr; Exit Preview
						</a>
					</div>
					<div
						v-if="(wsTab !== POST_SUBMISSION_VIEW && workspace && (workspace.s === 's' || workspace.closed)) || assignmentPastDeadline"
						class="main-header-message">
						<div>
							<div>
								<template v-if="workspace.s === 's'">You submitted this assignment</template>
								<template v-if="workspace.s !== 's'">This assignment ended</template>
								<template v-if="workspace.ts || assignmentPastDeadline">
									on {{ (workspace.ts || assignment.deadline) | date }} at {{ (workspace.ts || assignment.deadline) | time }}
								</template>
								<overdue-icon v-if="assignmentPastDueDate"></overdue-icon>
							</div>
						</div>
					</div>
					<div
						class="main-header-right"
						:class="{
							'hidden-toolbar': isToolbarHidden
						}">
						<div
							class="toolbar-hide-tab"
							@click="toggleToolbar"
							aria-label="Toggle toolbar">
							<i
								class="fa-solid"
								:class="{
									'fa-angles-up': isToolbarHidden,
									'fa-angles-down': !isToolbarHidden
								}"
								aria-hidden="true"></i>
						</div>
						<div class="main-header-actions">
							<button
								@click="toggleDistractionFree()"
								v-tooltip="'Expand for distraction free'"
								class="button -outline toolbar-button distraction-free-button"
								:class="{ '-active': distractionFreeView }">
								<i
									class="fa-solid fa-maximize"
									aria-hidden="true"></i>
							</button>
							<button
								v-if="wsTab !== EBOOK_VIEW"
								@click="nextFontSizeClass()"
								v-tooltip="'Increase text size'"
								class="button -outline toolbar-button font-size-button">
								<i
									class="icon-font-size"
									aria-hidden="true"></i>
							</button>
							<button
								v-if="offlineMode === false && submittedAnnotationsCount > 0 && workspace && workspace.s === 's'"
								v-tooltip="'Toggle annotations'"
								@click="toggleAnnotations"
								class="button -outline toolbar-button">
								<show-hide-markup-icon :showing="showAnnotations"> </show-hide-markup-icon>
							</button>
							<button
								v-if="isContentView && paragraphCount > 1 && wsTab !== VIDEO_VIEW"
								v-tooltip="'Show paragraph numbers'"
								class="button -outline toolbar-button"
								@click="toggleParagraphNumbers"
								:class="{ '-active': paragraphsNumbered }">
								<i class="fa-solid fa-paragraph"></i>
							</button>
							<button
								v-if="!isMobile && !isIpadOS && isContentView"
								@click="toggleSentenceFrame()"
								v-tooltip="'Use guided reading strip'"
								class="button -outline toolbar-button"
								:class="{ '-active': showSentenceFrame }">
								<sentence-frame-icon></sentence-frame-icon>
							</button>
							<button
								v-if="allowSplitScreen"
								v-tooltip="'Use split screen'"
								class="button -outline toolbar-button split-screen-button"
								@click="toggleSplitScreen"
								:class="{ '-active': showSplitPanel }">
								<i
									class="fa-solid fa-table-columns"
									aria-hidden="true"></i>
							</button>
						</div>
						<div
							class="toolbar-hide-tab distraction-hide-tab"
							@click="toggleToolbar"
							aria-label="Toggle toolbar">
							<i
								class="fa-solid"
								:class="{
									'fa-angles-up': !isToolbarHidden,
									'fa-angles-down': isToolbarHidden
								}"
								aria-hidden="true"></i>
						</div>
						<div
							v-if="(wsTab === BOOK_OF_KNOWLEDGE_VIEW || splitScreen) && isEll && ellFlag !== 'en'"
							class="language-toggle-container">
							<language-toggle
								:set-language="setLanguage"
								:student-language="ellFlag">
							</language-toggle>
						</div>
					</div>
					<tab-container
						model-key="wsTab"
						class="main-header-mobile-nav">
						<button
							v-for="tab in contentTabs"
							:key="tab.id"
							:data-tab="tab.id"
							:disabled="tab.disabled"
							v-tooltip="tab.disabled && tab.disabledTooltip"
							@click="workspaceTabClick"
							class="tooltip-bottom"
							:class="{
								active: wsTab === tab.id
							}">
							{{ tab.text }}
						</button>
					</tab-container>
					<button
						v-if="product.productType === 'P' && workspace && workspace.s === 'a' && workspace.opts.qs.length === 0"
						class="button paired-text-no-question-set-submit"
						@click="submitAssignment">
						Submit
					</button>
				</div>
			</div>
			<div
				class="wrapper"
				v-if="wsTab === RESULTS_VIEW || wsTab === WORD_DETECTIVE_VIEW || ([TEXT_VIEW, DECODABLES_VIEW].includes(wsTab) && product.productType === 'AAD')">
				<div
					v-if="workspace && workspace.s !== 's' && !workspace.closed && !assignmentPastDeadline"
					style="float: left"
					class="text-muted">
					This assignment is in progress.
				</div>
			</div>
		</header>
		<template v-if="workspace">
			<div
				class="wrapper workspace-body"
				:class="{
					'split-screen': showSplitPanel
				}"
				ref="wsTabPanel"
				:data-panel="wsTab">
				<tab-panel
					class="block-tabs content-panel"
					:tabs="contentTabs"
					:tab-key="'wsTab'"
					:on-tab-click="workspaceTabClick"
					:enable-snap-to-top="!splitScreen"
					ref="rightTabs">
					<sentence-frame
						slot="tab-panel-body"
						class="content-panel-body"
						:class="[fontSizeClass, customFontClass]"
						ref="contentContainer"
						:show="showSentenceFrame && isContentView">
						<div
							v-if="[TEXT_VIEW, DECODABLES_VIEW].includes(wsTab) && isAADWorkspace && articleList && articleList.length > 1"
							class="article-single-main-nav padding-top-large row">
							<div
								v-for="article in articleList"
								:key="article.uuid"
								class="small-4 columns text-ellipsis end keep-padding"
								:title="article.title">
								<a
									v-on:click.prevent="selectArticle(article)"
									:class="article.uuid === subArticle.uuid ? 'active' : ''"
									>{{ article.title }}</a
								>
							</div>
						</div>
						<component
							:is="componentsForSubView[wsTab]"
							:workspace="workspace"
							:offline-mode="offlineMode"
							:assignment="assignment"
							:show-annotations="showAnnotations"
							:product="product"
							:sub-article="subArticle"
							:paragraphs-numbered="paragraphsNumbered"
							:route-context="routeContext"
							:submit-assignment="submitAssignment"
							:assignment-done="assignmentDone"
							:must-rate-vocab-words-now="mustRateVocabWordsNow"
							:show-vocabulary-activities="showVocabularyActivities"
							:assignment-submitted="assignmentSubmitted"
							:assignment-past-deadline="assignmentPastDeadline"
							:question-sets-ordered-and-filtered="questionSetsOrderedAndFiltered"
							:assignment-id="assignmentId"
							:aid="assignmentId"
							:questions-blurb="questionsBlurb"
							:scored-question-sets="assessment && assessment.scoring ? assessment.scoring.questionSets : null"
							:ell-bok-directions-label="i18n.raadELLBOKDirectionsLabel"
							:ell-bok-instruction-text="i18n.raadELLBOKWrite"
							:ell-bok-instruction-audio-url="i18n.raadELLBOKWriteAudio"
							:ell-lang="i18n.languageCode"
							:ell-rtl="i18n.rtl"
							:is-ell="isEll"
							:on-next-question-set="onNextQuestionSet"
							:is-demo="isTeacher"
							:is-teacher="isTeacher"
							:marked-done="markedDone"
							@next-workspace-tab="onNextWorkspaceTab">
						</component>
					</sentence-frame>
				</tab-panel>
				<tab-panel
					v-if="showSplitPanel"
					:tabs="splitTabs"
					:tab-key="'splitTab'"
					:on-tab-click="workspaceTabClick"
					:enable-snap-to-top="false"
					ref="leftTabs"
					class="split-panel block-tabs">
					<div
						slot="tab-panel-body"
						class="split-panel-body"
						:class="[fontSizeClass, splitTab, customFontClass]">
						<component
							:is="componentsForSubView[splitTab]"
							:workspace="workspace"
							:assignment="assignment"
							:product="product"
							:sub-article="subArticle"
							:route-context="routeContext"
							:submit-assignment="submitAssignment"
							:assignment-done="assignmentDone"
							:assignment-submitted="assignmentSubmitted"
							:assignment-past-deadline="assignmentPastDeadline"
							:question-sets-ordered-and-filtered="questionSetsOrderedAndFiltered"
							:assignment-id="assignmentId"
							:questions-blurb="questionsBlurb"
							:scored-question-sets="assessment && assessment.scoring ? assessment.scoring.questionSets : null"
							:ell-bok-directions-label="i18n.raadELLBOKDirectionsLabel"
							:ell-bok-instruction-text="i18n.raadELLBOKWrite"
							:ell-bok-instruction-audio-url="i18n.raadELLBOKWriteAudio"
							:ell-lang="i18n.languageCode"
							:ell-rtl="i18n.rtl"
							:is-ell="isEll"
							:on-next-question-set="onNextQuestionSet"
							:is-demo="isTeacher"
							:is-teacher="isTeacher">
						</component>
					</div>
				</tab-panel>
			</div>
		</template>
		<modal
			id="modal-teacher-end-preview"
			has-form>
			<div slot="header-container">
				<h2>End Preview</h2>
			</div>
			<div slot="content">
				<a
					:href="`/assignment/${workspacePreviewClassCode}/${assignmentId}/classresults`"
					class="button">
					End Preview
				</a>
			</div>
		</modal>
		<modal
			id="modal-submit-assignment"
			has-form>
			<template slot="header-container">
				<h2>
					<template v-if="isAADWorkspace">
						<template v-if="workspace.opts.bok && !product.decodableTeacherEdition"> Submit Book of Knowledge </template>
						<template v-else> Done Reading </template>
					</template>
					<template v-else> Submit Assignment </template>
				</h2>
			</template>
			<template slot="content">
				<template v-if="confirmSubmitDialogData">
					<h4 v-if="confirmSubmitDialogData.bokBlurb">{{ confirmSubmitDialogData.bokBlurb }}</h4>
					<h4 v-if="confirmSubmitDialogData.vaBlurb">{{ confirmSubmitDialogData.vaBlurb }}</h4>
					<h4 v-if="confirmSubmitDialogData.qsetBlurb">{{ confirmSubmitDialogData.qsetBlurb }}</h4>
					<h4 v-if="confirmSubmitDialogData.overallBlurb">{{ confirmSubmitDialogData.overallBlurb }}</h4>
				</template>
				<form-submitter
					class="form"
					novalidate
					form-name="formParams"
					:form-data.sync="formParams"
					:submit-to="doSubmitAssignment">
					<div
						v-if="needsGoogleAuth"
						class="form-row">
						<span
							class="button"
							@click.stop="authenticateWithGoogle">
							Authenticate with Google
						</span>
					</div>
					<auto-form-row
						:label="'Submit' + (confirmSubmitDialogData && confirmSubmitDialogData.anyway ? ' Anyway' : '')"
						type="submitWithSurround"
						row-class="button"
						:isdisabled="needsGoogleAuth">
						<template
							slot="post-submit"
							v-if="isAADWorkspace">
							<a
								class="button -outline"
								data-modal-close>
								Go Back
							</a>
						</template>
					</auto-form-row>
				</form-submitter>
			</template>
		</modal>
		<modal id="pairtext-instructions">
			<template slot="header-container">
				<h2 v-if="product.subType === 8001">Spanish-English Paired Text</h2>
				<h2 v-else-if="!product.hasVideo">Paired Text</h2>
				<h2 v-else-if="product.hasVideo">Paired Video</h2>
				<div class="text-right">
					<language-toggle
						v-if="product.subType !== 8000"
						class="language-select margin-top-xs"
						:set-language="setLanguage"
						:student-language="product.languages && product.languages.find(lang => lang !== 'en')">
					</language-toggle>
				</div>
			</template>
			<template slot="content">
				<div
					v-if="product.subType !== 8000"
					aria-live="polite">
					<div v-html="i18n.languageCode === 'en' ? product.description : product.translatedDescription"></div>
					<p v-text="i18n.workspacePTInstructions"></p>
				</div>
				<h4 v-else-if="product.hasVideo">Please read the passage and watch the video to answer the questions.</h4>
				<h4 v-else>Please read both passages to answer the questions.</h4>

				<button
					class="button -centered text-uppercase"
					data-modal-close
					v-text="i18n.ok"></button>
			</template>
		</modal>
	</main>
</template>
